import { ComponentProps, useCallback } from 'react';
import { useLanguage } from 'context/AppContext';
import theme from 'App.theme';
import Button from 'components/Button';
import { TURNAROUND_CARD_HEADER_HEIGHT } from './TurnaroundCard';
import { useTurnaroundDestinationSelectedEntities } from '../hooks/useTurnaroundDestinationSelectedEntities';
import { useTurnaroundOriginSelectedEntities } from '../hooks/useTurnaroundOriginSelectedEntities';
import { useTurnaroundDestinationEntities } from '../hooks/useTurnaroundDestinationEntities';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';
import { useDelivery } from 'api/hooks/useDelivery';
import { formatDate } from 'components/DatePicker/DateInput';
import { TurnaroundEntity, TurnaroundEntityImplant, TurnaroundEntitySet } from '../models';
import { useTurnaroundOriginEntities } from '../hooks/useTurnaroundOriginEntities';

// Padding to align just below the TurnaroundMoveEntities header
const PADDING_TOP = TURNAROUND_CARD_HEADER_HEIGHT + parseInt(theme.layout.spacing.tiny.replace('px', ''));

type Props = ComponentProps<'div'>;

export const TurnaroundMoveEntities = ({ ...props }: Props) => {
  const lang = useLanguage()[0].TurnaroundMoveEntities;

  const [turnaroundDestinationEntities, setTurnaroundDestinationEntities] = useTurnaroundDestinationEntities();
  const [turnaroundOriginSelectedEntities, setTurnaroundOriginSelectedEntities] = useTurnaroundOriginSelectedEntities();
  const [turnaroundDestinationSelectedEntities, setTurnaroundDestinationSelectedEntities] =
    useTurnaroundDestinationSelectedEntities();
  const [turnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const { sets: originSets, implants: originImplants } = useTurnaroundOriginEntities();

  // Analytics
  const { data: originDelivery } = useDelivery(turnaroundOriginDeliveryId);
  const { data: currentUser } = useCurrentUser();

  const trackMoveEntitiesEvent = useCallback(
    async ({ setIds, implantIds, eventType }: { setIds: string[]; implantIds: string[]; eventType: EventType }) => {
      // Analytics
      if (!turnaroundOriginDeliveryId) {
        throw new Error('Turnaround origin delivery ID is not defined');
      }

      if (!originDelivery) {
        throw new Error('Origin delivery is not defined');
      }

      if (!currentUser) {
        throw new Error('Current user is not defined');
      }

      await trackEvent(eventType, {
        current_user: currentUser.userId,
        tenant_name: currentUser.tenantName,
        supplier: originDelivery.supplier.name || '',
        order_number: originDelivery.orderNumber || '',
        surgery_reference: originDelivery.surgery.surgeryReference || '',
        delivery_number: turnaroundOriginDeliveryId.toString(),
        surgeon: originDelivery.surgery?.surgeonName || '',
        surgery_date: originDelivery.surgery?.surgeryDate ? formatDate(originDelivery?.surgery?.surgeryDate) : '',
        set_ids: setIds,
        has_implants: Boolean(implantIds.length),
      });
    },
    [currentUser, originDelivery, turnaroundOriginDeliveryId],
  );

  const onSelectAll = useCallback(async () => {
    // Add the available sets to the destination sets
    // members move with their consolidated set, never on their own
    const availableSetKitInstanceIds = originSets
      .filter(set => !set.consolidatedIntoKitInstanceId)
      .map(set => set.kitInstanceId);
    const availableSetEntities = availableSetKitInstanceIds.map(
      setKitInstanceId => ({ type: 'set', setKitInstanceId } as TurnaroundEntity),
    );
    const availableImplantInstanceIds = originImplants.map(implant => implant.implantInstanceId);
    const availableImplantEntities = availableImplantInstanceIds.map(
      implantInstanceId => ({ type: 'implant', implantInstanceId } as TurnaroundEntity),
    );
    const availableEntities: TurnaroundEntity[] = [...availableSetEntities, ...availableImplantEntities];

    setTurnaroundDestinationEntities([...turnaroundDestinationEntities, ...availableEntities]);

    // Reset any selected origin sets
    setTurnaroundOriginSelectedEntities([]);

    await trackMoveEntitiesEvent({
      setIds: availableSetKitInstanceIds,
      implantIds: availableImplantInstanceIds,
      eventType: EventType.TurnaroundMoveOriginEntities,
    });
  }, [
    originImplants,
    originSets,
    setTurnaroundDestinationEntities,
    setTurnaroundOriginSelectedEntities,
    trackMoveEntitiesEvent,
    turnaroundDestinationEntities,
  ]);

  const onSelect = useCallback(async () => {
    // Add the selected origin entities to the destination entities
    setTurnaroundDestinationEntities([...turnaroundDestinationEntities, ...turnaroundOriginSelectedEntities]);

    // Reset any selected origin entities
    setTurnaroundOriginSelectedEntities([]);

    await trackMoveEntitiesEvent({
      setIds: turnaroundOriginSelectedEntities
        .filter(entity => entity.type === 'set')
        .map(entity => (entity as TurnaroundEntitySet).setKitInstanceId),
      implantIds: turnaroundOriginSelectedEntities
        .filter(entity => entity.type === 'implant')
        .map(entity => (entity as TurnaroundEntityImplant).implantInstanceId),
      eventType: EventType.TurnaroundMoveOriginEntities,
    });
  }, [
    setTurnaroundDestinationEntities,
    setTurnaroundOriginSelectedEntities,
    trackMoveEntitiesEvent,
    turnaroundDestinationEntities,
    turnaroundOriginSelectedEntities,
  ]);

  const onDeselectAll = useCallback(async () => {
    // Remove the sets from the destination sets
    setTurnaroundDestinationEntities([]);

    // Reset any selected destination sets
    setTurnaroundDestinationSelectedEntities([]);

    await trackMoveEntitiesEvent({
      setIds: turnaroundDestinationEntities
        .filter(entity => entity.type === 'set')
        .map(entity => (entity as TurnaroundEntitySet).setKitInstanceId),
      implantIds: turnaroundDestinationEntities
        .filter(entity => entity.type === 'implant')
        .map(entity => (entity as TurnaroundEntityImplant).implantInstanceId),
      eventType: EventType.TurnaroundMoveDestinationEntities,
    });
  }, [
    setTurnaroundDestinationSelectedEntities,
    setTurnaroundDestinationEntities,
    trackMoveEntitiesEvent,
    turnaroundDestinationEntities,
  ]);

  const onDeselect = useCallback(async () => {
    // Remove the selected destination entities from the destination entities
    setTurnaroundDestinationEntities(prev =>
      prev.filter(
        destinationEntity =>
          !turnaroundDestinationSelectedEntities.some(
            selectedEntity =>
              selectedEntity.type === destinationEntity.type &&
              (selectedEntity.type === 'set'
                ? (selectedEntity as TurnaroundEntitySet).setKitInstanceId ===
                  (destinationEntity as TurnaroundEntitySet).setKitInstanceId
                : (selectedEntity as TurnaroundEntityImplant).implantInstanceId ===
                  (destinationEntity as TurnaroundEntityImplant).implantInstanceId),
          ),
      ),
    );

    // Reset any selected destination entities
    setTurnaroundDestinationSelectedEntities([]);

    await trackMoveEntitiesEvent({
      setIds: turnaroundDestinationSelectedEntities
        .filter(entity => entity.type === 'set')
        .map(entity => (entity as TurnaroundEntitySet).setKitInstanceId),
      implantIds: turnaroundDestinationSelectedEntities
        .filter(entity => entity.type === 'implant')
        .map(entity => (entity as TurnaroundEntityImplant).implantInstanceId),
      eventType: EventType.TurnaroundMoveDestinationEntities,
    });
  }, [
    setTurnaroundDestinationSelectedEntities,
    setTurnaroundDestinationEntities,
    trackMoveEntitiesEvent,
    turnaroundDestinationSelectedEntities,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.layout.spacing.medium,
        paddingTop: PADDING_TOP,
        paddingBottom: theme.layout.spacing.tiny,
      }}
      {...props}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.spacing.small,
        }}
      >
        <Button colors="white on GREY3C" disabled={!originSets.length && !originImplants.length} onClick={onSelectAll}>
          {lang.selectAll}
        </Button>

        <Button colors="white on GREY3C" disabled={!turnaroundOriginSelectedEntities.length} onClick={onSelect}>
          {lang.select}
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.spacing.small,
        }}
      >
        <Button colors="white on GREY3C" disabled={!turnaroundDestinationEntities.length} onClick={onDeselectAll}>
          {lang.deselectAll}
        </Button>

        <Button colors="white on GREY3C" disabled={!turnaroundDestinationSelectedEntities.length} onClick={onDeselect}>
          {lang.deselect}
        </Button>
      </div>
    </div>
  );
};
